import { useTranslation } from "react-i18next";
import { TbSend } from "react-icons/tb";
import { MdOutlineWarningAmber } from "react-icons/md";
import { DetailCard, DetailRow } from "src/components/Details/Details";
import { Invoice } from "src/types";

const DUNNING_OPTIONS: { value: number; key: string; modifier: string; badge?: string }[] = [
    { value: 0, key: "none",      modifier: "grey"             },
    { value: 1, key: "friendly",  modifier: "green",  badge: "M1" },
    { value: 2, key: "demanding", modifier: "orange", badge: "M2" },
    { value: 3, key: "final",     modifier: "red",    badge: "M3" },
    { value: 4, key: "legal",     modifier: "blue"             },
];

interface InvoiceDatesCardProps {
    invoice: Invoice | null;
    dueDate: string;
    setDueDate: (v: string) => void;
    dunning: number;
    setDunning: (v: number) => void;
    dunningPaused: boolean;
    onDunningPausedChange: (v: boolean) => void;
    onResendDunning: () => void;
    /** Unsaved changes / save in flight — resend always uses the stored level. */
    resendDisabled: boolean;
    isPaid: boolean;
    isWrittenOff: boolean;
    readOnly?: boolean;
}

export function InvoiceDatesCard({ invoice, dueDate, setDueDate, dunning, setDunning, dunningPaused, onDunningPausedChange, onResendDunning, resendDisabled, isPaid, isWrittenOff, readOnly = false }: InvoiceDatesCardProps) {
    const { t } = useTranslation();
    const isOverdue = !isPaid && !isWrittenOff && dueDate && new Date(dueDate) < new Date();
    const isSent = !!invoice?.sentDate;
    const savedLevel = invoice?.dunning ?? 0;
    const showResend = isSent && !isPaid && !isWrittenOff && !readOnly && savedLevel >= 1 && savedLevel <= 3;
    const savedPaused = invoice?.dunningPaused ?? false;
    // Lowering the level restarts automatic dunning from the day it's saved — the next level
    // follows after its configured gap (backend utils/dunningSchedule.ts). Said before saving.
    const pendingDowngrade = dunning < savedLevel;
    const showDowngradeWarning = !isPaid && !isWrittenOff && isSent && pendingDowngrade;
    const resendHint = savedPaused
        ? t("invoicePage.resendDunningPausedHint")
        : resendDisabled
            ? t("invoicePage.resendDunningUnsavedHint")
            : undefined;

    const todayIso = () => new Date().toISOString().slice(0, 10);

    return (
        <DetailCard title={t("invoicePage.datesStatus")}>
            <DetailRow
                label={t("invoicePage.createdSent")}
                value={
                    <span>
                        {invoice?.creationDate ? new Date(invoice.creationDate).toLocaleDateString() : "—"}
                        {" → "}
                        {invoice?.sentDate ? new Date(invoice.sentDate).toLocaleDateString() : t("common.notSent")}
                    </span>
                }
            />
            <DetailRow
                label={t("invoicePage.dueDate")}
                value={
                    invoice?.sentDate && !isPaid && !isWrittenOff && !isOverdue && !readOnly ? (
                        <input
                            className={`invoice-input invoice-input--date${isOverdue ? " invoice-input--overdue" : ""}`}
                            type="date"
                            aria-label={t("invoicePage.dueDate")}
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                        />
                    ) : (
                        dueDate
                            ? new Date(dueDate).toLocaleDateString()
                            : (invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "-")
                    )
                }
            />
            <DetailRow
                label={t("table.dunning")}
                value={
                    isPaid || isWrittenOff ? "—" : (() => {
                        return (
                            <div className="invoice-dunning-chips" role="radiogroup" aria-label={t("table.dunning")}>
                                {DUNNING_OPTIONS.map((o) => {
                                    const label = t(`filters.dunningLevels.${o.key}`);
                                    const active = dunning === o.value;
                                    return (
                                        <button
                                            key={o.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            disabled={!isSent || readOnly}
                                            className={`invoice-dunning-chip${active ? ` invoice-dunning-chip--active invoice-dunning-chip--${o.modifier}` : ""}`}
                                            onClick={() => {
                                                if (dunning === 0 && o.value !== 0) setDueDate(todayIso());
                                                setDunning(o.value);
                                            }}
                                        >
                                            {o.badge && <span className={`dunning__badge dunning__badge--${o.modifier}`}>{o.badge}</span>}
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })()
                }
            />
            {showDowngradeWarning && (
                <div className="lx-options__banner lx-options__banner--warn invoice-dunning-warning">
                    <MdOutlineWarningAmber size={16} />
                    <span>{t("invoicePage.dunningDowngradeWarning")}</span>
                </div>
            )}
            {!isPaid && !isWrittenOff && (
                <DetailRow
                    label={t("invoicePage.pauseDunning")}
                    value={
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={dunningPaused}
                                disabled={!isSent || readOnly}
                                onChange={(e) => onDunningPausedChange(e.target.checked)}
                            />
                            <span className="toggle-switch__track" />
                        </label>
                    }
                />
            )}
            {showResend && (
                <DetailRow
                    label={t("invoicePage.resendDunning")}
                    value={
                        <button
                            type="button"
                            className="invoice-dunning-resend"
                            onClick={onResendDunning}
                            disabled={savedPaused || resendDisabled}
                            title={resendHint}
                        >
                            <TbSend />
                            {t("invoicePage.resendDunningBtn")}
                        </button>
                    }
                />
            )}
            {isPaid && invoice?.paidAt && (
                <DetailRow
                    label={t("invoicePage.paymentReceivedDate")}
                    value={new Date(invoice.paidAt).toLocaleDateString()}
                />
            )}
        </DetailCard>
    );
}
